import React from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadOutlinedIcon from '@mui/icons-material/UploadOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import PendingImageCropEditor from './PendingImageCropEditor';
import { CropAreaPixels, PendingCropMode, PendingImageAdjustments, PendingImageItem } from './pendingUploadTypes';

interface Props {
  descriptionLabel: string;
  disabled?: boolean;
  item: PendingImageItem;
  showDescriptionField?: boolean;
  onAdjustmentsChange: (localId: string, nextAdjustments: PendingImageAdjustments) => void;
  onCropChange: (localId: string, nextCropArea: CropAreaPixels, nextZoom: number) => void;
  onCropModeChange: (localId: string, nextCropMode: PendingCropMode) => void;
  onDescriptionChange: (localId: string, value: string) => void;
  onRemove: (localId: string) => void;
  onReset: (localId: string) => void;
  onUpload: (localId: string) => void;
}

export default function PendingImageTile({
  descriptionLabel,
  disabled = false,
  item,
  showDescriptionField = true,
  onAdjustmentsChange,
  onCropChange,
  onCropModeChange,
  onDescriptionChange,
  onRemove,
  onReset,
  onUpload,
}: Props) {
  const isUploading = item.status === 'uploading';
  const isFailed = item.status === 'failed';
  const snapSliderValue = (value: number) => (Math.abs(value) <= 4 ? 0 : value);
  const adjustmentControls: Array<{
    key: keyof PendingImageAdjustments;
    label: string;
  }> = [
    { key: 'brightness', label: 'Brightness' },
    { key: 'contrast', label: 'Contrast' },
    { key: 'warmth', label: 'Warmth' },
  ];
  const fixedCropLabel =
    item.fixedAspectRatio >= 1
      ? `Fixed ${Math.round(item.fixedAspectRatio * 9)}:9`
      : `Fixed 9:${Math.round(9 / item.fixedAspectRatio)}`;

  return (
    <Card
      variant="outlined"
      sx={{
        minHeight: 470,
        borderColor: isFailed ? 'error.light' : 'info.light',
        borderStyle: 'dashed',
      }}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
        <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={1}>
          <Stack alignItems="center" direction="row" spacing={1} sx={{ minWidth: 0, flexWrap: 'wrap' }}>
            <Chip
              color={isFailed ? 'error' : 'info'}
              icon={isFailed ? <WarningAmberOutlinedIcon /> : undefined}
              label={isFailed ? 'Upload failed' : isUploading ? 'Uploading' : 'Pending upload'}
              size="small"
            />
          </Stack>

          <Stack alignItems="center" direction="row" spacing={1}>
            <Button
              color="error"
              disabled={disabled || isUploading}
              size="small"
              startIcon={<DeleteOutlineIcon />}
              variant="text"
              onClick={() => onRemove(item.localId)}
            >
              Remove
            </Button>
          </Stack>
        </Stack>

        <PendingImageCropEditor disabled={disabled || isUploading} item={item} onCropChange={onCropChange} />

        <Box
          sx={{
            px: 1.5,
            py: 1.25,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'grey.50',
          }}
        >
          <Stack spacing={1}>
            <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={2}>
              <Typography sx={{ fontWeight: 600 }} variant="caption">
                Crop mode
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={item.cropMode}
                onChange={(_, nextValue: PendingCropMode | null) => {
                  if (!nextValue) {
                    return;
                  }

                  onCropModeChange(item.localId, nextValue);
                }}
              >
                <ToggleButton disabled={disabled || isUploading} value="fixed">
                  {fixedCropLabel}
                </ToggleButton>
                <ToggleButton disabled={disabled || isUploading} value="freeform">
                  Freeform
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            {adjustmentControls.map((control) => (
              <Box key={control.key}>
                <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={2}>
                  <Typography sx={{ fontWeight: 600 }} variant="caption">
                    {control.label}
                  </Typography>
                  <Typography color="text.secondary" variant="caption">
                    {item.adjustments[control.key] > 0 ? '+' : ''}
                    {item.adjustments[control.key]}
                  </Typography>
                </Stack>

                <Slider
                  disabled={disabled || isUploading}
                  max={100}
                  min={-100}
                  size="small"
                  step={1}
                  sx={{
                    mt: -0.25,
                    mb: -0.5,
                  }}
                  value={item.adjustments[control.key]}
                  onChange={(_, value) => {
                    if (Array.isArray(value)) {
                      return;
                    }

                    onAdjustmentsChange(item.localId, {
                      ...item.adjustments,
                      [control.key]: snapSliderValue(value),
                    });
                  }}
                />
              </Box>
            ))}

            <Stack alignItems="center" direction="row" justifyContent="flex-end">
              <Button
                disabled={disabled || isUploading}
                size="small"
                sx={{ minWidth: 0, px: 0.5 }}
                variant="text"
                onClick={() => onReset(item.localId)}
              >
                Reset
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Stack spacing={0.5}>
          <Typography noWrap variant="subtitle2">
            {item.filename}
          </Typography>
        </Stack>

        {showDescriptionField ? (
          <TextField
            disabled={disabled || isUploading}
            fullWidth
            label={descriptionLabel}
            maxRows={3}
            minRows={1}
            multiline
            size="small"
            value={item.description}
            onChange={(event) => onDescriptionChange(item.localId, event.target.value)}
          />
        ) : null}

        {item.errorMessage ? <Alert severity="error">{item.errorMessage}</Alert> : null}

        <Box sx={{ mt: 'auto' }}>
          <Button
            disabled={disabled || isUploading}
            fullWidth
            startIcon={<UploadOutlinedIcon />}
            variant="contained"
            onClick={() => onUpload(item.localId)}
          >
            {isFailed ? 'Retry upload' : isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
